import * as Yup from 'yup';

import { hasValue } from '@core/utils/equality';

import type { Company, CompanyWireFormat } from '../types/api/Company/Company';
import Enums from '../types/api/Enums';
import { MarketSummary } from '../types/api/MarketSummary/MarketSummary';
import { User } from '../types/api/User/User';
import { FormikAddress, FormikNullableIntegrationSummary } from '../utils/formikHelpers';
import { NIL_UUID } from '../utils/uuid';
import {
  TestForUndefined,
  YupAddress,
  YupBoolean,
  YupEnum,
  YupNullableEnum,
  YupNullableReference,
  YupNullableString,
  YupReference,
  type YupSchemaNullableReferenceType,
  YupSchemaReferenceType,
  YupString,
} from '../utils/yupHelpers';

type TemporaryAny = any;

type DispatchCustomerFormikType = YupSchemaNullableReferenceType & {
  displayOnly: {
    creditStatusCode: string | null;
    creditStatusDescription: string | null;
  };
};

type DispatchCustomersFormikType = {
  [key: string]: DispatchCustomerFormikType;
};

export type CompaniesBulkEditFormikType = {
  category: Enums.CompanyCategory | null;
  markets: YupSchemaReferenceType[];
  user: YupSchemaNullableReferenceType;
};

export type CompaniesBulkEditRequestPayload = {
  id: string;
  user?: Pick<User, 'id'>;
  marketSummaries?: Pick<MarketSummary, 'id'>[];
  category?: Company['category'];
};

export type CompanyFormikType = Omit<
  CompanyWireFormat,
  'dispatchCustomers' | 'marketSummaries' | 'user' | 'tiers'
> & {
  dispatchCustomers: DispatchCustomersFormikType;
  user: YupSchemaNullableReferenceType;
  divisions: YupSchemaReferenceType[];
  markets: YupSchemaReferenceType[];
};

export const defaultDispatchCustomer: DispatchCustomerFormikType = {
  id: null,
  option: null,
  displayOnly: { creditStatusCode: null, creditStatusDescription: null },
};

export const CompaniesBulkEditSchemaFormik: Yup.SchemaOf<CompaniesBulkEditFormikType> =
  Yup.object().shape({
    category: YupNullableEnum(Enums.CompanyCategory, 'Category'),
    markets: Yup.array().of(YupReference('Market')),
    user: YupNullableReference('User'),
  });

const DispatchCustomerValueSchema = YupNullableReference('Dispatch Customer')
  .concat(
    Yup.object({
      displayOnly: Yup.object({
        creditStatusCode: YupNullableString('Credit status code'),
        creditStatusDescription: YupNullableString('Credit status description'),
      }).default({ creditStatusCode: null, creditStatusDescription: null }),
    }),
  )
  .default(defaultDispatchCustomer)
  .nullable();

export const DispatchCustomersSchema = Yup.lazy((value: unknown) => {
  const obj = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const shape = Object.fromEntries(Object.keys(obj).map((k) => [k, DispatchCustomerValueSchema]));

  return Yup.object().shape(shape).noUnknown(false).nullable().default({});
}) as unknown as Yup.SchemaOf<DispatchCustomersFormikType>;

type CompanySchemaBaseType = Omit<CompanyFormikType, 'id' | 'externalID'>;

export type RequiredCompanyFieldsInfo = {
  markets: boolean;
};

export const NewCompanySchema = (
  requiredFields: RequiredCompanyFieldsInfo,
): Yup.SchemaOf<Omit<CompanyFormikType, 'id' | 'externalID'>> =>
  Yup.object({
    name: YupString('Name'),
    isArchived: YupBoolean('Archived'),
    alternateID: YupNullableString('Alternate ID'),
    address: YupAddress('Address'),
    category: YupEnum(Enums.CompanyCategory, 'Category'),
    notes: YupNullableString('Notes (Internal Only)'),
    dispatchCustomers: DispatchCustomersSchema,
    user: YupNullableReference('User'),
    integrationSummary: Yup.mixed().nullable(),
    markets: Yup.array()
      .of(YupReference('Market'))
      .min(requiredFields.markets ? 1 : 0),
    divisions: Yup.array().of(YupReference('Division')),
  }).test(TestForUndefined('CompanySchema'));

export const CompanySchema = NewCompanySchema({ markets: false });

export const FormikCompany = (companyData: Partial<Company> | undefined): CompanyFormikType => {
  const dispatchCustomers: {
    [key: string]: typeof DispatchCustomerValueSchema;
  } = {};
  if (hasValue(companyData?.dispatchCustomers)) {
    companyData.dispatchCustomers.map((dc) => {
      // only need this during integration transition
      if (!hasValue(dc.integrationSummary)) {
        return;
      }

      dispatchCustomers[dc.integrationSummary.id] = {
        ...dc,
        option: {
          value: dc.id,
          label: dc.name,
          // TODO: remove after integrationID is not null
          sublabels: [dc.integrationSummary?.name || '', dc.externalId],
        },
        displayOnly: {
          creditStatusCode: dc.creditStatusCode ?? null,
          creditStatusDescription: dc.creditStatusDescription ?? null,
        },
      };
    });
  }

  return {
    id: companyData?.id ?? NIL_UUID,
    isArchived: companyData?.isArchived ?? false,
    externalID: companyData?.externalID ?? null,
    name: companyData?.name ?? '',
    alternateID: companyData?.alternateID ?? null,
    address: FormikAddress(companyData?.address),
    category: companyData?.category ?? Enums.CompanyCategory.Contractor,
    notes: companyData?.notes ?? null,
    integrationSummary: FormikNullableIntegrationSummary(companyData?.integrationSummary),
    dispatchCustomers,
    markets: (companyData?.marketSummaries ?? []).map((m) => ({
      id: m.id,
      option: {
        value: m.id,
        label: m.name,
      },
    })),

    user: {
      id: companyData?.user?.id ?? null,
      option:
        companyData?.user === undefined || companyData?.user === null
          ? null
          : {
              value: companyData.user.id,
              label: `${companyData.user.firstName} ${companyData.user.lastName}`,
            },
    },
    // todo: add divisions from companyData
    divisions: [],
  };
};

export const NewCompanySchemaFormik = (): Yup.ObjectSchema<CompanySchemaBaseType & TemporaryAny> =>
  // ts-expect-error Other fields will be added later
  Yup.object().shape({
    name: YupString('Name'),
    isArchived: YupBoolean('Archived'),
    alternateID: YupNullableString('Alternate ID'),
    address: YupAddress('Address'),
    category: YupEnum(Enums.CompanyCategory, 'Category'),
    notes: YupNullableString('Notes (Internal Only)'),
    dispatchCustomers: DispatchCustomersSchema,
    market: YupNullableReference('Market'),
    user: YupNullableReference('User'),
    displayOnly: Yup.array().of(
      Yup.object({
        creditStatusCode: YupNullableString('Credit status code'),
        creditStatusDescription: YupNullableString('Credit status description'),
      }),
    ),
  });
